import { ListLocationFlagsResponse } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_service_api.pb';
import { AutoMessageType_Slug, Rule } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import {
  CreateRuleRequest,
  DeleteRuleRequest,
  ListAutoMessageTypesRequest,
  ListRulesRequest,
  ListRulesResponse,
  RuleHistoryRequest,
  RuleRequest,
  ToggleRuleRequest,
  UpdateRuleRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/service.pb';
import { LocationsFeaturesRequest } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { ListDataSourcesRequest } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useQueries, useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { http, HttpError } from '@frontend/fetch';
import { SchemaFeatureFlags, SchemaLocationService, SchemaPlatformLocationFeature } from '@frontend/schema';
import { SchemaAutoRules, SchemaIntegrationsService } from './service';

export const queryKeys = {
  base: 'messages',
  allCustomizationFlags: (locationIds: string[]) => [queryKeys.base, 'customization-flags', locationIds],
  autoruleHistory: (ruleId: string) => [queryKeys.base, 'autorule-history', ruleId],
  featureFlag: (locationId: string) => [queryKeys.base, 'feature-flags', locationId],
  listRulesBase: () => [queryKeys.base, 'list-rules'],
  listRules: (request: ListRulesRequest) => [...queryKeys.listRulesBase(), request],
  listDataSourcesByLocationID: (request: ListDataSourcesRequest) => [queryKeys.base, 'data-sources', request],
  locationData: (locationId: string) => [queryKeys.base, 'location-data', locationId],
  messageTypes: (request: ListAutoMessageTypesRequest) => [queryKeys.base, 'message-types', request],
  rule: (ruleId: string) => [queryKeys.base, 'autorule', ruleId],
  specificCustomizationFlags: (request: LocationsFeaturesRequest) => [queryKeys.base, 'customization-flags', request],
};

export const sortLocationIds = (locationIds?: string[]) =>
  locationIds ? locationIds.sort((a, b) => (a > b ? 1 : -1)) : undefined;

const defaultOptions = {
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export type GroupedListRules = Record<AutoMessageType_Slug, Rule[]>;
export const groupListRulesBySlug = (response: ListRulesResponse) =>
  (response.rules || []).reduce((acc, item) => {
    const type = item?.autoMessageType?.slug as AutoMessageType_Slug;
    return {
      ...acc,
      [type]: acc[type] ? [...acc[type], item] : [item],
    };
  }, {} as GroupedListRules);

/**
 * React hook to fetch a list of rules using the provided request parameters.
 *
 * This hook leverages React Query's `useQuery` to fetch data from the `SchemaAutoRules.ListRules` API.
 * It merges the provided `request` with default values (`skipFilterValidation: true`, `summaryOnly: true`)
 * before making the API call.
 *
 * The query will only execute if both `orgId` and `businessGroups` are present in the request.
 *
 * @template T - The type of the data returned by the query.
 * @param request - The request parameters for listing rules.
 * @param options - Optional React Query options.
 * @returns The result of the query, including status, data, and error information.
 *
 * @remarks
 * The cache for this hook is updated based on `orgId`, `businessGroups`, and the default values for `skipFilterValidation` and `summaryOnly`.
 */
export const useListRules = <T>(
  request: ListRulesRequest,
  options?: UseQueryOptions<ListRulesResponse, unknown, T>
) => {
  const defaultRequest: Partial<ListRulesRequest> = {
    skipFilterValidation: true,
    summaryOnly: true,
  };

  const finalRequest = { ...defaultRequest, ...request };
  return useQuery({
    ...defaultOptions,
    queryKey: queryKeys.listRules({ ...finalRequest, businessGroups: sortLocationIds(finalRequest.businessGroups) }),
    queryFn: () => SchemaAutoRules.ListRules(finalRequest),
    enabled: !!finalRequest.orgId && !!finalRequest.businessGroups?.length,
    ...options,
  });
};

export const useRule = (request: RuleRequest): UseQueryResult<Rule | undefined, HttpError> =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.rule(request.ruleId!),
    queryFn: () => SchemaAutoRules.Rule(request),
    enabled: !!(request.ruleId && request.ruleId !== 'new' && request.orgId),
    select: (data) => data.rule,
    refetchOnMount: true,
  });

export const useRuleHistory = (request: RuleHistoryRequest) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.autoruleHistory(request.ruleId!),
    queryFn: () => SchemaAutoRules.RuleHistory(request),
    enabled: !!request.ruleId && !!request.orgId,
    select: (data) => data.rules,
  });

export const useListAutoMessageTypes = (request: ListAutoMessageTypesRequest) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.messageTypes({ ...request, businessGroupIds: sortLocationIds(request.businessGroupIds) }),
    queryFn: () => SchemaAutoRules.ListAutoMessageTypes(request),
    select: (data) => data.messageTypes,
    enabled: !!request.orgId && !!request?.businessGroupIds?.length,
  });

// TODO: we are using the ANY strategy here - but I think this should be an ALL strategy.
/**
 * React hook to fetch all customization flags for given location IDs with no specification on which feature flag.
 *
 * This hook uses the `SchemaPlatformLocationFeature.LocationsFeatures` API to retrieve all features for the provided location IDs,
 * and then filters them to return only the active or promoted features.
 *
 * @param locationIds - An array of location IDs for which to fetch customization flags.
 * @returns An array of active or promoted features for the given location IDs.
 */
export const useAllCustomizationFlags = (locationIds: string[]) =>
  useQuery({
    queryKey: queryKeys.allCustomizationFlags(locationIds),
    queryFn: () => SchemaPlatformLocationFeature.LocationsFeatures({ locationIds }),
    enabled: !!locationIds.length,
    select: (data) => {
      return [
        ...new Set(
          Object.values(data.locationsFeatures || {})
            .map((item) => item.features ?? [])
            .flat()
            .filter((feature) => feature.state === State.ACTIVE || feature.state === State.PROMOTE)
            .map((item) => item.featureEnum) as Feature[]
        ),
      ];
    },
  });

/**
 * React hook to fetch specific customization flag for given location IDs.
 *
 * This hook uses the `SchemaPlatformLocationFeature.LocationsFeatures` API to retrieve features for the provided location IDs,
 * and organizes the enabled and disabled location IDs for the specified features.
 * 
 * Example usage:
 *   const { data: customizations = {} } = AutoRulesQueries.useSpecificCustomizationFlags({
    features: [Feature.TRUELARK_AI_MESSAGING, Feature.ANALYTICS_HOME_ICON],
    locationIds: locationIdsWithoutParent,
  });
  * it should return an object like { TRUELARK_AI_MESSAGING: { enabledLocationIds: [], disabledLocationIds: [] }, ANALYTICS_HOME_ICON: enabledLocationIds: [], disabledLocationIds: [] } indicating which locations have the active feature.
  
  * @param request - An object containing the features to check and the location IDs to query.
*/
const CUSTOMIZATION_STATES = [State.ACTIVE];
const DEFAULT_FEATURES_MAP = { enabledLocationIds: [], disabledLocationIds: [] };
export const useSpecificCustomizationFlags = (request: Required<LocationsFeaturesRequest>) =>
  useQuery({
    queryKey: queryKeys.specificCustomizationFlags(request),
    queryFn: () => SchemaPlatformLocationFeature.LocationsFeatures(request),
    enabled: !!request.locationIds?.length,
    select: (data) => {
      const featuresMap: Partial<Record<Feature, { enabledLocationIds: string[]; disabledLocationIds: string[] }>> = {};
      Object.entries(data.locationsFeatures || {})
        .map(([locationId, { features }]) => features?.map((feature) => ({ ...feature, locationId })) ?? [])
        .reduce((acc, locationData) => {
          for (let i = 0; i < locationData.length; i++) {
            const feature = locationData[i];
            const featureName = feature.featureEnum;
            const featureState = feature.state;
            const locationId = feature.locationId;

            if (!featureName || !featureState) continue;

            // organize the enabled and disabled locationIds for easy access for each consumer
            if (request.features?.includes(featureName) && CUSTOMIZATION_STATES.includes(featureState)) {
              acc[featureName] = {
                ...(acc[featureName] || DEFAULT_FEATURES_MAP),
                enabledLocationIds: [...(acc[featureName]?.enabledLocationIds || []), locationId],
              };
            } else {
              acc[featureName] = {
                ...(acc[featureName] || DEFAULT_FEATURES_MAP),
                disabledLocationIds: [...(acc[featureName]?.disabledLocationIds || []), locationId],
              };
            }
          }
          return acc;
        }, featuresMap);

      return featuresMap;
    },
  });

// gets all the feature flags for a single organization
export const useFeatureFlags = (locationIds: string[]) =>
  useQueries(
    locationIds.map((locationId) => ({
      queryKey: queryKeys.featureFlag(locationId),
      queryFn: () => SchemaFeatureFlags.ListLocationFlags({ locationId }),
      enabled: !!locationId,
      select: (data: ListLocationFlagsResponse) => {
        const flags = (data.flags || []).filter((flag) => flag.value);
        return { locationId, flags };
      },
    }))
  );

export const useGetLocationData = (locationId: string) =>
  useQuery({
    queryKey: queryKeys.locationData(locationId),
    queryFn: () => SchemaLocationService.GetLocation({ locationId }),
    enabled: !!locationId,
  });

// TODO: move these to a mutations file and refactor the useMutation calls in the components
export const createRule = (request: CreateRuleRequest) => SchemaAutoRules.CreateRule(request);
export const deleteRule = (request: DeleteRuleRequest) => SchemaAutoRules.DeleteRule(request);
export const toggleRule = (request: ToggleRuleRequest) => SchemaAutoRules.ToggleRule(request);
export const updateRule = (request: UpdateRuleRequest) => SchemaAutoRules.UpdateRule(request);

export const useListDataSourcesByLocationID = (request: ListDataSourcesRequest) => {
  return useQuery({
    queryKey: queryKeys.listDataSourcesByLocationID(request),
    queryFn: () => SchemaIntegrationsService.ListDataSourcesByLocationID(request),
    enabled: !!request.locationId,
  });
};

export const postAudioLibraryDialerPreview = (request: { text: string; lang?: string }) =>
  http.post('/phone/audio-library/v1/dialer/preview', request, { responseType: 'blob' });
